import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { KeyRound, Save, CheckCircle2, Mic, Cloud, Rocket } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [elevenKey, setElevenKey] = useState("");
  const [googleKey, setGoogleKey] = useState("");
  const [systemeKey, setSystemeKey] = useState("");
  const [status, setStatus] = useState({ elevenlabs_key_set: false, google_key_set: false, systeme_key_set: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setStatus(data);
    } catch (e) {
      console.warn("Failed to load provider settings:", e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const body = {};
    if (elevenKey.trim()) body.elevenlabs_key = elevenKey.trim();
    if (googleKey.trim()) body.google_api_key = googleKey.trim();
    if (systemeKey.trim()) body.systeme_key = systemeKey.trim();
    if (Object.keys(body).length === 0) {
      toast.info("Enter a key to save");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings", body);
      setStatus(data);
      setElevenKey("");
      setGoogleKey("");
      setSystemeKey("");
      toast.success("Provider keys saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save keys");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div className="fade-up flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <KeyRound size={24} />
        </div>
        <div>
          <h1 className="font-head text-4xl font-bold">Provider Settings</h1>
          <p className="text-muted-foreground">
            Bring your own keys to unlock premium media features. Keys are stored securely and never shared.
          </p>
        </div>
      </div>

      <Card className="border-border p-8">
        <div className="space-y-8">
          <ProviderField
            icon={Mic}
            title="ElevenLabs"
            desc="Powers AI voiceover generation for your reels."
            help="Get your key at elevenlabs.io → Profile → API Keys"
            isSet={status.elevenlabs_key_set}
            value={elevenKey}
            onChange={setElevenKey}
            testid="elevenlabs-key-input"
            placeholder="sk_..."
          />
          <div className="border-t border-border" />
          <ProviderField
            icon={Cloud}
            title="Google Cloud"
            desc="Optional key for Google-powered video & image generation."
            help="Create a key in Google Cloud Console → APIs & Services → Credentials"
            isSet={status.google_key_set}
            value={googleKey}
            onChange={setGoogleKey}
            testid="google-key-input"
            placeholder="AIza..."
          />
          <div className="border-t border-border" />
          <ProviderField
            icon={Rocket}
            title="systeme.io"
            desc="Push your Full Funnel Content Pack straight into a new systeme.io funnel."
            help="Get your key at systeme.io → Settings → MCP & API keys → Public API keys → Create"
            isSet={status.systeme_key_set}
            value={systemeKey}
            onChange={setSystemeKey}
            testid="systeme-key-input"
            placeholder="Your systeme.io public API key"
          />
        </div>

        <div className="mt-8 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-settings-btn"
            className="rounded-full bg-primary px-6 hover:bg-primary/90">
            <Save size={18} className="mr-1" /> {saving ? "Saving..." : "Save keys"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ProviderField({ icon: Icon, title, desc, help, isSet, value, onChange, testid, placeholder }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-primary">
          <Icon size={18} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-head text-lg font-bold">{title}</h3>
            {isSet && (
              <span data-testid={`${testid}-status`} className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-xs font-semibold text-accent">
                <CheckCircle2 size={12} /> Connected
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{desc}</p>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>{isSet ? `Replace ${title} key` : `${title} API key`}</Label>
        <Input type="password" value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder} data-testid={testid} autoComplete="off" />
        <p className="text-xs text-muted-foreground">{help}</p>
      </div>
    </div>
  );
}
